import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Card,
  CardContent,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import styled from 'styled-components';
import Sidebar from './Sidebar';
import ResponsiveAppBar from './ResponsiveAppBar';
import { MotionButton } from './common/MotionButton';
import DashboardCalculator from './DashboardCalculator';
import DeleteIcon from '@mui/icons-material/Delete';

const ERROR_DISPLAY_TIME = 200; // 2 milliseconds

const Dashboard = () => {
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const [showCalculator, setShowCalculator] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [estimateToDelete, setEstimateToDelete] = useState(null);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [approvingEstimate, setApprovingEstimate] = useState(false);
  const [approvalProgress, setApprovalProgress] = useState(0);

  const fetchEstimates = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      
      if (!token || !userId) {
        navigate('/login');
        return;
      }

      // Use the correct endpoint for estimates
      const response = await fetch(`http://localhost:5001/api/estimates/user/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch estimates');
      }

      const serverEstimates = await response.json();
      
      // Format and sort estimates
      const formattedEstimates = serverEstimates.map(estimate => ({
        ...estimate,
        amount: parseFloat(estimate.amount || 0).toLocaleString(),
        projectDetails: {
          service: estimate.projectDetails?.service || 'Unknown Service',
          dimensions: estimate.projectDetails?.dimensions || '',
          area: estimate.projectDetails?.area || '',
          zipCode: estimate.projectDetails?.zipCode || ''
        },
        status: estimate.status || 'pending',
        createdAt: new Date(estimate.createdAt).toLocaleDateString()
      }));

      // Sort by date, newest first
      formattedEstimates.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );

      setEstimates(formattedEstimates);
    } catch (err) {
      console.error('Error fetching estimates:', err);
      setError('Failed to load estimates');
    } finally {
      setLoading(false);
    }
  }, [navigate]); // Only depend on navigate

  const checkProfileCompletion = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');

      if (!token || !userId) {
        return false;
      }

      const response = await fetch(`http://localhost:5001/api/homeowners/profile/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      console.log('Profile data for completion check:', data);

      // Check if all required fields are filled
      const isComplete = Boolean(
        data.firstName?.trim() &&
        data.lastName?.trim() &&
        data.phone?.trim() &&
        data.address?.street?.trim() &&
        data.address?.city?.trim() &&
        data.address?.state?.trim() &&
        data.address?.zipCode?.trim()
      );

      console.log('Profile completion status:', isComplete);
      setShowProfilePrompt(!isComplete);

      return isComplete;

    } catch (error) {
      console.error('Error checking profile completion:', error);
      setShowProfilePrompt(true);
      return false;
    }
  }, []);

  useEffect(() => {
    fetchEstimates();
    checkProfileCompletion();
  }, [fetchEstimates, checkProfileCompletion, refreshTrigger]);

  // Check profile completion on mount and when profile might change
  useEffect(() => {
    checkProfileCompletion();
  }, [checkProfileCompletion]);

  // Recheck periodically
  useEffect(() => {
    const interval = setInterval(checkProfileCompletion, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [checkProfileCompletion]);

  const handleStatusUpdate = async (estimateId, newStatus) => {
    try {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');

      if (!token || !userId) {
        throw new Error('Authentication required');
      }

      // For local estimates (no _id)
      if (!estimateId) {
        const pendingEstimate = localStorage.getItem('pendingEstimate');
        const projectData = localStorage.getItem('projectData');

        if (!pendingEstimate || !projectData) {
          throw new Error('No estimate data found');
        }

        const parsedEstimate = JSON.parse(pendingEstimate);
        const parsedProjectData = JSON.parse(projectData);

        // This format should match what CalculatorResults saves
        const estimateData = {
          userId,
          amount: parseFloat(parsedEstimate.amount),
          projectDetails: {
            service: parsedProjectData.projectType,
            dimensions: parsedProjectData.length && parsedProjectData.width ? 
              `${parsedProjectData.length}x${parsedProjectData.width}` : '',
            area: parsedProjectData.totalSquareFootage || '',
            zipCode: parsedProjectData.zipCode || ''
          },
          status: newStatus
        };

        const response = await fetch('http://localhost:5001/api/estimates', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(estimateData)
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || 'Failed to save estimate');
        }

        // Clear localStorage only after successful save
        localStorage.removeItem('pendingEstimate');
        localStorage.removeItem('projectData');
      } else {
        // Update existing estimate
        const response = await fetch(`http://localhost:5001/api/estimates/${estimateId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ status: newStatus })
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || 'Failed to update estimate');
        }
      }

      // Refresh estimates after successful update
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error updating estimate:', error);
      setError(error.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleNewEstimate = () => {
    setShowCalculator(true);
  };

  const handleEstimateComplete = () => {
    setShowCalculator(false);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleApproveEstimate = async (estimateId) => {
    try {
      setApprovingEstimate(true);
      setApprovalProgress(0);
      setError(null);

      const progressInterval = setInterval(() => {
        setApprovalProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const token = localStorage.getItem('authToken');
      console.log('Approving estimate:', estimateId);
      setApprovalProgress(50);

      const response = await fetch(`http://localhost:5001/api/estimates/${estimateId}/approve`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setApprovalProgress(70);

      if (!response.ok) {
        throw new Error('Failed to approve estimate');
      }

      setApprovalProgress(90);
      await fetchEstimates();
      setMessage('Estimate approved successfully!');
      setTimeout(() => setMessage(null), ERROR_DISPLAY_TIME);

      clearInterval(progressInterval);
      setApprovalProgress(100);

      setTimeout(() => {
        setApprovingEstimate(false);
        setApprovalProgress(0);
      }, 500);

    } catch (error) {
      console.error('Error approving estimate:', error);
      setError('Failed to approve estimate: ' + error.message);
      setTimeout(() => setError(null), ERROR_DISPLAY_TIME);
      setApprovingEstimate(false);
      setApprovalProgress(0);
    }
  };

  const handleDeleteClick = (estimate) => {
    setEstimateToDelete(estimate);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`http://localhost:5001/api/estimates/${estimateToDelete._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete estimate');
      }

      // Remove estimate from state
      setEstimates(estimates.filter(e => e._id !== estimateToDelete._id));
      setDeleteDialogOpen(false);
      setEstimateToDelete(null);
      
      // Show success message
      setMessage('Estimate deleted successfully');
      setTimeout(() => setMessage(''), 3000);

    } catch (error) {
      console.error('Error deleting estimate:', error);
      setError('Failed to delete estimate');
    }
  };

  const formatServiceName = (service) => {
    if (!service) return 'Unknown Service';
    return service
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const ProfilePrompt = styled.div`
    background: #FFF3E0;
    border: 1px solid #FFB74D;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    p {
      margin: 0;
      color: #E65100;
    }

    button {
      background: #FF5722;
      &:hover {
        background: #F4511E;
      }
    }
  `;

  const LoadingOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  `;

  const ProgressContainer = styled.div`
    width: 300px;
    text-align: center;
  `;

  const ProgressText = styled.div`
    margin: 1rem 0;
    color: #FF5722;
    font-size: 1.2rem;
    font-weight: 500;
  `;

  const renderEstimateActions = (estimate) => {
    if (estimate.status === 'pending') {
      return (
        <ActionButtons>
          <ApproveButton
            onClick={() => handleApproveEstimate(estimate._id)}
            disabled={approvingEstimate}
          >
            {approvingEstimate ? 'Approving...' : 'Approve Estimate'}
          </ApproveButton>
          <DeclineButton 
            onClick={() => handleStatusUpdate(estimate._id, 'rejected')}
            variant="outlined"
            fullWidth
          >
            Decline
          </DeclineButton>
        </ActionButtons>
      );
    }
    return null;
  };

  return (
    <StyledDashboardContainer>
      <Sidebar />
      <StyledDashboardContent>
        <ResponsiveAppBar />
        <StyledMain>
          {message && (
            <MessageAlert success>
              {message}
            </MessageAlert>
          )}
          {error && (
            <MessageAlert error>
              {error}
            </MessageAlert>
          )}
          {showProfilePrompt && (
            <ProfilePrompt>
              <p>Please complete your profile to get the most out of our services!</p>
              <Button
                variant="contained"
                color="warning"
                onClick={() => navigate('/profile')}
              >
                Update Profile
              </Button>
            </ProfilePrompt>
          )}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Title>Your Estimates</Title>
            
            {!showCalculator && (
              <NewEstimateButton
                onClick={handleNewEstimate}
                variant="contained"
              >
                Create New Estimate
              </NewEstimateButton>
            )}

            {showCalculator && (
              <DashboardCalculator 
                onEstimateComplete={handleEstimateComplete}
              />
            )}

            {loading ? (
              <LoadingContainer>
                <StyledCircularProgress />
                <LoadingText>Loading your estimates...</LoadingText>
              </LoadingContainer>
            ) : error ? (
              <ErrorContainer>
                <ErrorIcon>⚠️</ErrorIcon>
                <ErrorText>{error}</ErrorText>
              </ErrorContainer>
            ) : estimates.length === 0 ? (
              <EmptyState>
                <EmptyIcon>📋</EmptyIcon>
                <EmptyText>No estimates yet. Get started by using our calculator!</EmptyText>
                <MotionButton
                  onClick={() => navigate('/calculator')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Create New Estimate
                </MotionButton>
              </EmptyState>
            ) : (
              <EstimatesGrid>
                {estimates.map((estimate, index) => (
                  <EstimateCard
                    key={index}
                    as={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <CardContent>
                      <ServiceType>
                        {formatServiceName(estimate?.projectDetails?.service || 'Unknown Service')}
                      </ServiceType>
                      
                      <Amount>
                        ${estimate?.amount?.toLocaleString() || 0}
                      </Amount>
                      
                      <ProjectDetails>
                        {estimate?.projectDetails?.dimensions && (
                          <DetailItem>
                            <DetailLabel>Dimensions:</DetailLabel>
                            <DetailValue>{estimate.projectDetails.dimensions}</DetailValue>
                          </DetailItem>
                        )}
                        
                        {estimate?.projectDetails?.area && (
                          <DetailItem>
                            <DetailLabel>Area:</DetailLabel>
                            <DetailValue>{estimate.projectDetails.area}</DetailValue>
                          </DetailItem>
                        )}

                        {estimate?.projectDetails?.zipCode && (
                          <DetailItem>
                            <DetailLabel>Location:</DetailLabel>
                            <DetailValue>ZIP: {estimate.projectDetails.zipCode}</DetailValue>
                          </DetailItem>
                        )}
                      </ProjectDetails>
                      
                      <StatusBadge status={estimate?.status || 'pending'}>
                        {(estimate?.status || 'pending').toUpperCase()}
                      </StatusBadge>
                      
                      {renderEstimateActions(estimate)}
                      
                      <CreatedAt>
                        Created: {new Date(estimate?.createdAt).toLocaleDateString()}
                      </CreatedAt>
                    </CardContent>
                    <IconButton 
                      className="delete-button"
                      onClick={() => handleDeleteClick(estimate)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </EstimateCard>
                ))}
              </EstimatesGrid>
            )}
          </motion.div>
        </StyledMain>
      </StyledDashboardContent>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Estimate</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this estimate? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {approvingEstimate && (
        <LoadingOverlay>
          <ProgressContainer>
            <LinearProgress 
              variant="determinate" 
              value={approvalProgress}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: '#ffebe6',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#FF5722',
                  borderRadius: 5
                }
              }}
            />
            <ProgressText>
              Approving estimate...
            </ProgressText>
          </ProgressContainer>
        </LoadingOverlay>
      )}
    </StyledDashboardContainer>
  );
};

const StyledDashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f5f5f5;
`;

const StyledDashboardContent = styled.main`
  flex: 1;
  margin-left: 250px;
  padding: 2rem;
  padding-top: calc(60px + 2rem);
  
  @media (max-width: 768px) {
    margin-left: 0;
    padding-top: calc(60px + 2rem);
  }
`;

const StyledMain = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
`;

const Title = styled.h1`
  margin-bottom: 2rem;
  color: #FF5722;
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 1rem;
`;

const StyledCircularProgress = styled(CircularProgress)`
  &.MuiCircularProgress-root {
    color: #FF5722;
  }
`;

const LoadingText = styled.p`
  color: #666;
  font-size: 1.1rem;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 3rem;
  background: #FFF3F0;
  border-radius: 16px;
  border: 1px solid #FFE0DB;
`;

const ErrorIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const ErrorText = styled.p`
  color: #D32F2F;
  font-size: 1.1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  color: #666;
  font-size: 1.2rem;
  margin-bottom: 2rem;
`;

const EstimatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;
  padding: 1rem;
`;

const EstimateCard = styled(Card)`
  border-radius: 16px;
  background: white;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 20px rgba(255, 87, 34, 0.1);
  }

  .delete-button {
    position: absolute;
    top: 8px;
    right: 8px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover .delete-button {
    opacity: 1;
  }
`;

const ServiceType = styled.h3`
  color: #333;
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const Amount = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #FF5722;
  margin: 1rem 0;
`;

const ProjectDetails = styled.div`
  background: #f8f8f8;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  color: #666;
  font-weight: 500;
`;

const DetailValue = styled.span`
  color: #333;
  font-weight: 600;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  margin: 1rem 0;
  
  ${props => {
    switch (props.status) {
      case 'approved': return `
        background: #E8F5E9;
        color: #2E7D32;
      `;
      case 'pending': return `
        background: #FFF3E0;
        color: #E65100;
      `;
      case 'rejected': return `
        background: #FFEBEE;
        color: #C62828;
      `;
      default: return `
        background: #F5F5F5;
        color: #666;
      `;
    }
  }}
`;

const ActionButtons = styled.div`
  display: flex;
  margin: 1rem 0;
`;

const DeclineButton = styled(Button)`
  && {
    color: #D32F2F;
    border: 1px solid #D32F2F;
    &:hover {
      background: #FFEBEE;
    }
  }
`;

const ApproveButton = styled(Button)`
  && {
    background: #FF5722;
    color: white;
    margin-right: 1rem;
    padding: 0.8rem 2rem;
    font-size: 1.1rem;
    
    &:hover {
      background: #F4511E;
    }
  }
`;

const CreatedAt = styled.div`
  color: #999;
  font-size: 0.875rem;
  margin-top: 1rem;
`;

const NewEstimateButton = styled(Button)`
  && {
    background: #FF5722;
    color: white;
    margin-bottom: 2rem;
    padding: 0.8rem 2rem;
    font-size: 1.1rem;
    
    &:hover {
      background: #F4511E;
    }
  }
`;

const MessageAlert = styled.div`
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 8px;
  text-align: center;
  background: ${props => props.success ? '#4CAF50' : '#f44336'};
  color: white;
  opacity: 1;
  transition: all 0.3s ease;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

export default Dashboard;